/**
 * Web Audio signal chain for the instrument.
 *
 *   oscillator bank -> timbre mix -> low-pass filter -> amplitude gain
 *     -> compressor/limiter -> analyser -> master gain -> destination
 *
 * Design rules that matter for a playable instrument:
 *
 *  - The oscillators are started once and never stopped while the instrument is active.
 *    All articulation happens through gain, so there are no restart clicks.
 *  - Every parameter change goes through `setTargetAtTime` or a short ramp. Raw per-frame
 *    assignment to an AudioParam is what makes hand-tracked synths sound like a fax machine.
 *  - The AudioContext is only created from an explicit user gesture, and is suspended
 *    whenever the page is hidden.
 */

import { MAX_OUTPUT_GAIN } from './volumeMapping';
import { clamp, clamp01 } from '../tracking/types';

export interface AudioEngineOptions {
  /** Time constant for frequency glide, seconds. Small enough to feel immediate. */
  frequencyTimeConstant?: number;
  /** Time constant for amplitude changes, seconds. */
  gainTimeConstant?: number;
  /** Fade applied on start/stop, seconds. */
  fadeSeconds?: number;
  /** Vibrato rate in Hz and depth in cents. */
  vibratoRate?: number;
  vibratoDepthCents?: number;
}

const DEFAULTS: Required<AudioEngineOptions> = {
  frequencyTimeConstant: 0.014,
  gainTimeConstant: 0.02,
  fadeSeconds: 0.12,
  vibratoRate: 5.4,
  vibratoDepthCents: 24,
};

export type AudioEngineState = 'idle' | 'starting' | 'running' | 'suspended' | 'closed';

export class AudioUnavailableError extends Error {
  constructor(message: string, options?: { cause?: unknown }) {
    super(message, options);
    this.name = 'AudioUnavailableError';
  }
}

/**
 * A gently bright periodic wave: strong fundamental, a soft second and third partial and a
 * whisper of the fifth. Cheaper than running four oscillators and it keeps the classic
 * theremin "voice" quality without the buzz of a raw sawtooth.
 */
const PARTIAL_REAL = new Float32Array([0, 0, 0, 0, 0, 0]);
const PARTIAL_IMAG = new Float32Array([0, 1, 0.22, 0.11, 0.045, 0.02]);

export class ThereminAudioEngine {
  private context: AudioContext | null = null;

  private readonly options: Required<AudioEngineOptions>;

  private engineState: AudioEngineState = 'idle';

  // Graph nodes; all null until `start()`.
  private mainOsc: OscillatorNode | null = null;

  private detuneOsc: OscillatorNode | null = null;

  private subOsc: OscillatorNode | null = null;

  private mainMix: GainNode | null = null;

  private detuneMix: GainNode | null = null;

  private subMix: GainNode | null = null;

  private timbreBus: GainNode | null = null;

  private filter: BiquadFilterNode | null = null;

  private amplitude: GainNode | null = null;

  private limiter: DynamicsCompressorNode | null = null;

  private analyser: AnalyserNode | null = null;

  private master: GainNode | null = null;

  private vibratoOsc: OscillatorNode | null = null;

  private vibratoDepth: GainNode | null = null;

  private levelBuffer: Uint8Array<ArrayBuffer> | null = null;

  private targetFrequency = 220;

  private targetGain = 0;

  private masterVolume = 0.7;

  private muted = false;

  private vibratoRateHz: number;

  /** Depth in cents at full amount, and the live 0..1 scaler applied to it. */
  private vibratoDepthCents: number;

  private vibratoAmount = 0;

  /** Last depth actually written to the AudioParam, so per-frame calls stay free. */
  private appliedVibratoCents = -1;

  constructor(options: AudioEngineOptions = {}) {
    this.options = { ...DEFAULTS, ...options };
    this.vibratoRateHz = this.options.vibratoRate;
    this.vibratoDepthCents = this.options.vibratoDepthCents;
  }

  get state(): AudioEngineState {
    return this.engineState;
  }

  get running(): boolean {
    return this.engineState === 'running';
  }

  get sampleRate(): number {
    return this.context?.sampleRate ?? 0;
  }

  /** Round-trip latency estimate in ms; 0 when unknown. */
  get latencyMs(): number {
    const ctx = this.context;
    if (!ctx) return 0;
    const base = typeof ctx.baseLatency === 'number' ? ctx.baseLatency : 0;
    const output = typeof ctx.outputLatency === 'number' ? ctx.outputLatency : 0;
    return (base + output) * 1000;
  }

  get audioContext(): AudioContext | null {
    return this.context;
  }

  /**
   * Create (or resume) the context and build the graph. MUST be called from a user gesture.
   * Idempotent: calling it while running just resumes a suspended context.
   */
  async start(): Promise<void> {
    if (this.engineState === 'running') return;
    if (this.engineState === 'suspended' && this.context) {
      await this.resume();
      return;
    }
    this.engineState = 'starting';

    const Ctor: typeof AudioContext | undefined =
      globalThis.AudioContext ??
      (globalThis as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!Ctor) {
      this.engineState = 'idle';
      throw new AudioUnavailableError('Web Audio is not supported in this browser.');
    }

    let ctx: AudioContext;
    try {
      ctx = new Ctor({ latencyHint: 'interactive' });
    } catch (error) {
      this.engineState = 'idle';
      throw new AudioUnavailableError('Could not create an audio context.', { cause: error });
    }
    this.context = ctx;

    try {
      this.buildGraph(ctx);
    } catch (error) {
      await this.dispose();
      throw new AudioUnavailableError('Could not build the audio graph.', { cause: error });
    }

    // Autoplay policies can leave a freshly created context suspended.
    if (ctx.state === 'suspended') {
      try {
        await ctx.resume();
      } catch {
        // Left suspended; `resume()` can be retried from another gesture.
      }
    }

    this.engineState = ctx.state === 'running' ? 'running' : 'suspended';
    this.applyMasterGain(this.options.fadeSeconds);
  }

  private buildGraph(ctx: AudioContext): void {
    const now = ctx.currentTime;
    const wave = ctx.createPeriodicWave(PARTIAL_REAL, PARTIAL_IMAG, {
      disableNormalization: false,
    });

    this.mainOsc = ctx.createOscillator();
    this.mainOsc.setPeriodicWave(wave);
    this.mainOsc.frequency.setValueAtTime(this.targetFrequency, now);

    // A slightly detuned triangle thickens the tone without adding audible beating.
    this.detuneOsc = ctx.createOscillator();
    this.detuneOsc.type = 'triangle';
    this.detuneOsc.frequency.setValueAtTime(this.targetFrequency, now);
    this.detuneOsc.detune.setValueAtTime(7, now);

    // A quiet sine an octave down gives the instrument body on small speakers.
    this.subOsc = ctx.createOscillator();
    this.subOsc.type = 'sine';
    this.subOsc.frequency.setValueAtTime(this.targetFrequency / 2, now);

    this.mainMix = ctx.createGain();
    this.mainMix.gain.setValueAtTime(0.72, now);
    this.detuneMix = ctx.createGain();
    this.detuneMix.gain.setValueAtTime(0.16, now);
    this.subMix = ctx.createGain();
    this.subMix.gain.setValueAtTime(0.12, now);

    this.timbreBus = ctx.createGain();
    this.timbreBus.gain.setValueAtTime(1, now);

    this.filter = ctx.createBiquadFilter();
    this.filter.type = 'lowpass';
    this.filter.frequency.setValueAtTime(this.filterCutoffFor(this.targetFrequency), now);
    this.filter.Q.setValueAtTime(0.6, now);

    this.amplitude = ctx.createGain();
    this.amplitude.gain.setValueAtTime(0, now);

    // Soft knee limiter: catches transients from fast hand moves, inaudible otherwise.
    this.limiter = ctx.createDynamicsCompressor();
    this.limiter.threshold.setValueAtTime(-10, now);
    this.limiter.knee.setValueAtTime(12, now);
    this.limiter.ratio.setValueAtTime(8, now);
    this.limiter.attack.setValueAtTime(0.004, now);
    this.limiter.release.setValueAtTime(0.18, now);

    this.analyser = ctx.createAnalyser();
    this.analyser.fftSize = 512;
    this.analyser.smoothingTimeConstant = 0.7;
    this.levelBuffer = new Uint8Array(new ArrayBuffer(this.analyser.fftSize));

    this.master = ctx.createGain();
    this.master.gain.setValueAtTime(0, now);

    this.vibratoOsc = ctx.createOscillator();
    this.vibratoOsc.type = 'sine';
    this.vibratoOsc.frequency.setValueAtTime(this.vibratoRateHz, now);
    this.vibratoDepth = ctx.createGain();
    this.appliedVibratoCents = this.vibratoDepthCents * this.vibratoAmount;
    this.vibratoDepth.gain.setValueAtTime(this.appliedVibratoCents, now);

    this.mainOsc.connect(this.mainMix).connect(this.timbreBus);
    this.detuneOsc.connect(this.detuneMix).connect(this.timbreBus);
    this.subOsc.connect(this.subMix).connect(this.timbreBus);

    this.timbreBus
      .connect(this.filter)
      .connect(this.amplitude)
      .connect(this.limiter)
      .connect(this.analyser)
      .connect(this.master)
      .connect(ctx.destination);

    // Every voice is modulated, including the sub: detune is in cents, so one shared control
    // signal moves them all by the same musical interval and the chord stays coherent.
    this.vibratoOsc.connect(this.vibratoDepth);
    this.vibratoDepth.connect(this.mainOsc.detune);
    this.vibratoDepth.connect(this.detuneOsc.detune);
    this.vibratoDepth.connect(this.subOsc.detune);

    this.mainOsc.start();
    this.detuneOsc.start();
    this.subOsc.start();
    this.vibratoOsc.start();
  }

  /** Keep the timbre consistent across the range by tracking the cutoff to the fundamental. */
  private filterCutoffFor(frequency: number): number {
    return clamp(frequency * 5.5 + 350, 500, 12_000);
  }

  /**
   * Set the sounding frequency. Uses an exponential approach so glides sound musical and
   * jitter is filtered by the time constant rather than stepping the parameter.
   */
  setFrequency(hz: number): void {
    if (!Number.isFinite(hz) || hz <= 0) return;
    this.targetFrequency = clamp(hz, 20, 12_000);
    const ctx = this.context;
    if (!ctx || !this.mainOsc || !this.detuneOsc || !this.subOsc || !this.filter) return;
    const now = ctx.currentTime;
    const tc = this.options.frequencyTimeConstant;
    this.mainOsc.frequency.setTargetAtTime(this.targetFrequency, now, tc);
    this.detuneOsc.frequency.setTargetAtTime(this.targetFrequency, now, tc);
    this.subOsc.frequency.setTargetAtTime(this.targetFrequency / 2, now, tc);
    this.filter.frequency.setTargetAtTime(this.filterCutoffFor(this.targetFrequency), now, tc * 3);
  }

  /**
   * Set the pre-master amplitude, 0..MAX_OUTPUT_GAIN.
   * @param immediate use a hard ramp instead of an exponential approach (panic / fade out)
   */
  setGain(gain: number, immediate = false): void {
    this.targetGain = clamp(gain, 0, MAX_OUTPUT_GAIN);
    const ctx = this.context;
    if (!ctx || !this.amplitude) return;
    const now = ctx.currentTime;
    const param = this.amplitude.gain;
    if (immediate) {
      param.cancelScheduledValues(now);
      param.setValueAtTime(param.value, now);
      param.linearRampToValueAtTime(this.targetGain, now + 0.03);
    } else {
      param.setTargetAtTime(this.targetGain, now, this.options.gainTimeConstant);
    }
  }

  setMasterVolume(volume: number): void {
    this.masterVolume = clamp01(volume);
    this.applyMasterGain();
  }

  setMuted(muted: boolean): void {
    if (this.muted === muted) return;
    this.muted = muted;
    this.applyMasterGain();
  }

  get isMuted(): boolean {
    return this.muted;
  }

  /**
   * Vibrato shape: speed in Hz and depth in cents *at full amount*.
   *
   * Depth is expressed in cents rather than Hz so the wobble is the same musical interval at
   * the bottom and the top of the range — a fixed Hz offset would be a wide vibrato low down
   * and inaudible high up.
   */
  setVibratoShape(rateHz: number, depthCents: number): void {
    if (Number.isFinite(rateHz)) this.vibratoRateHz = clamp(rateHz, 0.1, 20);
    if (Number.isFinite(depthCents)) this.vibratoDepthCents = clamp(depthCents, 0, 200);
    const ctx = this.context;
    if (ctx && this.vibratoOsc) {
      this.vibratoOsc.frequency.setTargetAtTime(this.vibratoRateHz, ctx.currentTime, 0.05);
    }
    this.applyVibratoDepth();
  }

  /**
   * Live vibrato depth scaler, 0..1. Safe to call every frame: the parameter is only touched
   * when the resulting depth actually moves, and then only through a time constant, so a
   * hand opening gradually is heard as the vibrato blooming rather than as steps.
   */
  setVibratoAmount(amount: number): void {
    const next = clamp01(amount);
    if (Math.abs(next - this.vibratoAmount) < 0.004) return;
    this.vibratoAmount = next;
    this.applyVibratoDepth();
  }

  get vibrato(): { rateHz: number; depthCents: number; amount: number } {
    return {
      rateHz: this.vibratoRateHz,
      depthCents: this.vibratoDepthCents,
      amount: this.vibratoAmount,
    };
  }

  private applyVibratoDepth(): void {
    const target = this.vibratoDepthCents * this.vibratoAmount;
    if (Math.abs(target - this.appliedVibratoCents) < 0.05) return;
    this.appliedVibratoCents = target;
    const ctx = this.context;
    if (!ctx || !this.vibratoDepth) return;
    this.vibratoDepth.gain.setTargetAtTime(target, ctx.currentTime, 0.09);
  }

  private applyMasterGain(fadeSeconds = 0.05): void {
    const ctx = this.context;
    if (!ctx || !this.master) return;
    const target = this.muted || this.engineState === 'idle' ? 0 : this.masterVolume;
    const now = ctx.currentTime;
    const param = this.master.gain;
    param.cancelScheduledValues(now);
    param.setValueAtTime(param.value, now);
    param.linearRampToValueAtTime(target, now + Math.max(0.01, fadeSeconds));
  }

  /** Instantaneous output level in 0..1, derived from the analyser. Allocation-free. */
  getOutputLevel(): number {
    const analyser = this.analyser;
    const buffer = this.levelBuffer;
    if (!analyser || !buffer) return 0;
    analyser.getByteTimeDomainData(buffer);
    let peak = 0;
    for (let i = 0; i < buffer.length; i += 4) {
      const sample = Math.abs((buffer[i] ?? 128) - 128);
      if (sample > peak) peak = sample;
    }
    return clamp01(peak / 128);
  }

  /** Fade to silence and suspend the context (page hidden, or user pressed Stop). */
  async suspend(): Promise<void> {
    const ctx = this.context;
    if (!ctx || this.engineState === 'closed' || this.engineState === 'idle') return;
    this.setGain(0, true);
    this.applyMasterGain(this.options.fadeSeconds);
    await waitMs(this.options.fadeSeconds * 1000 + 20);
    try {
      await ctx.suspend();
      this.engineState = 'suspended';
    } catch {
      // Some browsers refuse to suspend; silence has already been applied.
    }
  }

  async resume(): Promise<void> {
    const ctx = this.context;
    if (!ctx || this.engineState === 'closed') return;
    try {
      await ctx.resume();
      this.engineState = ctx.state === 'running' ? 'running' : 'suspended';
      this.applyMasterGain(this.options.fadeSeconds);
    } catch (error) {
      throw new AudioUnavailableError('Could not resume audio.', { cause: error });
    }
  }

  /** Immediate, click-free silence without tearing down the graph. */
  panic(): void {
    this.setGain(0, true);
    const ctx = this.context;
    if (!ctx || !this.master) return;
    const now = ctx.currentTime;
    this.master.gain.cancelScheduledValues(now);
    this.master.gain.setValueAtTime(this.master.gain.value, now);
    this.master.gain.linearRampToValueAtTime(0, now + 0.04);
  }

  /** Stop and release everything. Safe to call repeatedly. */
  async dispose(): Promise<void> {
    const ctx = this.context;
    this.engineState = 'closed';
    if (!ctx) return;
    try {
      this.panic();
      await waitMs(60);
      for (const osc of [this.mainOsc, this.detuneOsc, this.subOsc, this.vibratoOsc]) {
        try {
          osc?.stop();
        } catch {
          // Already stopped.
        }
      }
      for (const node of [
        this.mainOsc,
        this.detuneOsc,
        this.subOsc,
        this.vibratoOsc,
        this.vibratoDepth,
        this.mainMix,
        this.detuneMix,
        this.subMix,
        this.timbreBus,
        this.filter,
        this.amplitude,
        this.limiter,
        this.analyser,
        this.master,
      ]) {
        try {
          node?.disconnect();
        } catch {
          // Ignore: disconnecting an already-detached node is harmless.
        }
      }
      await ctx.close();
    } catch {
      // Nothing actionable; the context is being discarded regardless.
    } finally {
      this.context = null;
      this.mainOsc = null;
      this.detuneOsc = null;
      this.subOsc = null;
      this.vibratoOsc = null;
      this.vibratoDepth = null;
      this.mainMix = null;
      this.detuneMix = null;
      this.subMix = null;
      this.timbreBus = null;
      this.filter = null;
      this.amplitude = null;
      this.limiter = null;
      this.analyser = null;
      this.master = null;
      this.levelBuffer = null;
    }
  }
}

function waitMs(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}
